import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const AVAILABLE_BRANCHES = [
  'Computer Science',
  'Information Technology',
  'Mechanical Engineering',
  'Civil Engineering',
  'Electrical Engineering',
  'Electronics Engineering',
];

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [customBranch, setCustomBranch] = useState('');
  const [customBranches, setCustomBranches] = useState([]);
  const { register: registerCollege, isLoading, error, clearError } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  const handleBranchToggle = (branch) => {
    setSelectedBranches(prev =>
      prev.includes(branch)
        ? prev.filter(b => b !== branch)
        : [...prev, branch]
    );
  };

  const handleAddCustomBranch = () => {
    const trimmedBranch = customBranch.trim();
    if (trimmedBranch === '') {
      toast.error('Please enter a branch name');
      return;
    }

    // Check if branch already exists in predefined or custom list
    const allBranches = [...AVAILABLE_BRANCHES, ...customBranches];
    if (allBranches.some(b => b.toLowerCase() === trimmedBranch.toLowerCase())) {
      toast.error('This branch already exists');
      return;
    }

    setCustomBranches(prev => [...prev, trimmedBranch]);
    setSelectedBranches(prev => [...prev, trimmedBranch]);
    setCustomBranch('');
    toast.success('Custom branch added');
  };

  const handleRemoveCustomBranch = (branch) => {
    setCustomBranches(prev => prev.filter(b => b !== branch));
    setSelectedBranches(prev => prev.filter(b => b !== branch));
  };

  const onSubmit = async (data) => {
    if (selectedBranches.length === 0) {
      toast.error('Please select at least one branch');
      return;
    }

    clearError();
    const collegeData = {
      ...data,
      branches: selectedBranches,
    };

    const result = await registerCollege(collegeData);

    if (result.success) {
      toast.success('College registered successfully!');
    } else {
      toast.error(result.error || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-2xl">🏛️</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Register Your College
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create an admin account for your college
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* College Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="label">
                College Name
              </label>
              <input
                id="name"
                type="text"
                className={`input ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="Enter college name"
                {...register('name', {
                  required: 'College name is required',
                  minLength: {
                    value: 2,
                    message: 'College name must be at least 2 characters',
                  },
                  maxLength: {
                    value: 200,
                    message: 'College name cannot exceed 200 characters',
                  },
                })}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="label">
                Admin Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`input ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="admin@college.edu"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address',
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="label">
                Contact Number
              </label>
              <input
                id="phone"
                type="tel"
                className={`input ${errors.phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="+1234567890"
                {...register('phone', {
                  required: 'Contact number is required',
                  pattern: {
                    value: /^\+?[\d\s-()]+$/,
                    message: 'Invalid phone number',
                  },
                })}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input pr-10 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                  placeholder="Create password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                className={`input ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="Confirm password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value =>
                    value === password || 'Passwords do not match',
                })}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label htmlFor="address" className="label">
                College Address
              </label>
              <textarea
                id="address"
                rows={3}
                className={`input ${errors.address ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="Enter full address"
                {...register('address', {
                  required: 'Address is required',
                  minLength: {
                    value: 10,
                    message: 'Address must be at least 10 characters',
                  },
                  maxLength: {
                    value: 500,
                    message: 'Address cannot exceed 500 characters',
                  },
                })}
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="label">
                Website (Optional)
              </label>
              <input
                id="website"
                type="url"
                className={`input ${errors.website ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="https://college.edu"
                {...register('website', {
                  pattern: {
                    value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                    message: 'Invalid website URL',
                  },
                })}
              />
              {errors.website && (
                <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
              )}
            </div>

            {/* Established Year */}
            <div>
              <label htmlFor="establishedYear" className="label">
                Established Year (Optional)
              </label>
              <input
                id="establishedYear"
                type="number"
                min="1800"
                max={new Date().getFullYear()}
                className={`input ${errors.establishedYear ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="2000"
                {...register('establishedYear', {
                  min: {
                    value: 1800,
                    message: 'Year must be after 1800',
                  },
                  max: {
                    value: new Date().getFullYear(),
                    message: 'Year cannot be in the future',
                  },
                })}
              />
              {errors.establishedYear && (
                <p className="mt-1 text-sm text-red-600">{errors.establishedYear.message}</p>
              )}
            </div>

            {/* Branches */}
            <div className="md:col-span-2">
              <label className="label">
                Available Branches <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Select all branches/departments available in your college
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {AVAILABLE_BRANCHES.map((branch) => (
                  <label
                    key={branch}
                    className="flex items-center space-x-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBranches.includes(branch)}
                      onChange={() => handleBranchToggle(branch)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{branch}</span>
                  </label>
                ))}
              </div>

              {/* Custom Branches Section */}
              {customBranches.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Custom Branches:</p>
                  <div className="flex flex-wrap gap-2">
                    {customBranches.map((branch) => (
                      <div
                        key={branch}
                        className="flex items-center space-x-2 px-3 py-1 bg-primary-50 border border-primary-200 rounded-full"
                      >
                        <span className="text-sm text-primary-700">{branch}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomBranch(branch)}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Custom Branch Input */}
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Add Custom Branch
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={customBranch}
                    onChange={(e) => setCustomBranch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomBranch();
                      }
                    }}
                    placeholder="Enter custom branch name"
                    className="flex-1 input text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomBranch}
                    className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Add
                  </button>
                </div>
              </div>

              {selectedBranches.length === 0 && (
                <p className="mt-1 text-sm text-red-600">Please select at least one branch</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex justify-center py-3"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Registering...
                </div>
              ) : (
                'Register College'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;